import { generateMetricsFromDomain, computeThroughputBasis } from '../metrics';
import { generateBom } from '../utils/generateBom';
import { createPricingStructure, deriveCostEfficiency } from '../utils/solutionPricing.jsx';
import { calculateSoftwarePricing, calculateHardwarePricing } from '../utils/pricingCalculator';
import { createPriceResolver } from '../pricing/priceResolver';
import { log } from '../utils/devLog';

/**
 * Derives metrics, pricing and BOM from a (typed domain, flat config) pair.
 *
 * Pulled verbatim out of useConfigurationLogic's debounced derivation effect
 * (CONF-112 Task 9) — the span from `generateMetricsFromDomain` through
 * `deriveCostEfficiency`. Excludes the list-price baseline
 * (computeBaselinePricing), which the hook computes from these outputs. The hook
 * and the MCP Lambda's headless pipeline both call this same function so a
 * configuration prices identically in either place.
 *
 * @param {Object} args
 * @param {Object} args.domain - Typed `DomainConfiguration`.
 * @param {Object} args.config - Flat config (`toFlatConfig(domain)`).
 * @param {Object} args.productDb - Product database for pricing/specs.
 * @param {Object} args.metricsDb - Metrics database for performance specs.
 * @param {Object} args.vendor - The active vendor.
 * @param {Object} [args.customPrices] - Exec price overrides, HARDWARE SKUs only.
 * @returns {{ metrics: Object, bom: Array, pricingData: Object }}
 */
export const deriveOutputs = ({ domain, config, productDb, metricsDb, vendor, customPrices }) => {
  // Calculate physical metrics
  // const metrics2 = generateMetrics(config, productDb); // OLD from generateMetrics.jsx
  const metrics = generateMetricsFromDomain(domain, productDb, metricsDb);

  // CONF-258: the raw per-node throughput rating/multiplier behind each
  // total, for exec Custom Metrics overrides. Attached immediately after
  // generateMetricsFromDomain, before anything else reads `metrics`.
  metrics.throughputBasis = computeThroughputBasis(domain, productDb, metricsDb);

  // Calculate pricing
  const pricingData = createPricingStructure(config, productDb, metrics);
  calculateSoftwarePricing(config, productDb, metrics, pricingData, vendor);
  const prices = createPriceResolver(productDb, config.quarter, customPrices ?? {});
  calculateHardwarePricing(config, productDb, metrics, pricingData, prices, vendor);
  //const calculatedPricing = calculateAllPricing(config, productDb, metrics, pricingData); // New idea to do it all in one
  log('=== PRICING CALCULATION COMPLETE ===');
  log('Pricing Data:', pricingData);

  // Generate BOM using calculated pricing
  const { bom } = generateBom(config, productDb, metrics, pricingData, vendor, prices);

  // Update metrics with final cost
  metrics.totalSolutionCost = pricingData.solution.grandTotal;
  metrics.totalSoftwareCost = pricingData.software.total;
  metrics.totalHardwareCost = pricingData.hardware.total;

  // Add the efficiency metrics
  Object.assign(metrics, deriveCostEfficiency(
    pricingData.solution.grandTotal, metrics, config.compressionRatio));
  // MORK TODO: Add more efficiency/cost metrics as defaults in generateMetrics

  return { metrics, bom, pricingData };
};
